import { Group, Text, Stack, ThemeIcon, Progress, Box } from '@mantine/core';
import { IconGenderMale, IconGenderFemale, type Icon } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { SectionCard } from './SectionCard';

export interface GenderSplit { male: number; female: number; total: number }

const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

/**
 * Jins bo'yicha taqsimot kartasi — erkak/ayol soni + foizi va ustunli diagramma.
 * `maleLabel`/`femaleLabel` — kontekstga qarab (O'g'il/Qiz, Ota/Ona, Erkak/Ayol).
 */
export function GenderCard({
  icon: TitleIcon, title, data, maleLabel, femaleLabel,
}: {
  icon: Icon;
  title: string;
  data: GenderSplit | undefined;
  maleLabel: string;
  femaleLabel: string;
}) {
  const { t } = useTranslation();
  const male = data?.male ?? 0;
  const female = data?.female ?? 0;
  const total = data?.total ?? 0;
  const malePct = pct(male, total);
  const femalePct = pct(female, total);

  const Row = ({ icon: RowIcon, color, label, count, percent }: { icon: Icon; color: string; label: string; count: number; percent: number }) => (
    <Group justify="space-between" wrap="nowrap">
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon size={30} radius="md" variant="light" color={color}><RowIcon size={17} /></ThemeIcon>
        <Text size="sm" fw={600}>{label}</Text>
      </Group>
      <Group gap={6} wrap="nowrap">
        <Text fw={800} fz="lg">{count}</Text>
        <Text size="sm" c="dimmed">({percent}%)</Text>
      </Group>
    </Group>
  );

  return (
    // Sarlavha uslubi qolgan kartalar bilan bir xil bo'lishi uchun SectionCard
    <SectionCard title={title} icon={TitleIcon} subtitle={t('common.itemsCount', { count: total })}>
      {/* Ustunli diagramma — erkak/ayol nisbati */}
      <Progress.Root size={14} mb="md" radius="xl">
        <Progress.Section value={malePct} color="blue" />
        <Progress.Section value={femalePct} color="pink" />
      </Progress.Root>

      <Stack gap="sm">
        <Row icon={IconGenderMale} color="blue" label={maleLabel} count={male} percent={malePct} />
        <Box style={{ borderTop: '1px solid var(--mantine-color-default-border)' }} />
        <Row icon={IconGenderFemale} color="pink" label={femaleLabel} count={female} percent={femalePct} />
      </Stack>
    </SectionCard>
  );
}
